import { addDays, diffDays, isBetween, toDayNumber } from './date'
import type { BbtLog, ISODate, LhLog, PeriodLog, SymptomLog } from './types'

/**
 * 周期预测引擎。纯函数、零依赖，前后端共用。
 *
 * 核心思路（和「上次 + 28 天」的区别）：
 *  1. 周期长度用近期加权 + 中位数混合估计，单次异常周期（生病、旅行、压力）
 *     不会把预测整体带偏。
 *  2. 样本少时向先验收缩，不会因为只记了一个周期就给出虚假的精确预测。
 *  3. 排卵日从「预测的下次经期」倒推，而不是从周期第一天正推 14 天。
 *     黄体期（排卵→经期）在同一个人身上相当稳定，卵泡期才是波动的来源，
 *     所以倒推明显更准。
 *  4. 预测永远带不确定区间，界面画成阴影带，不假装能精确到某一天。
 *
 * 【重要】本引擎的易孕窗/安全期不可作为避孕依据。
 */

// ── 可调参数 ────────────────────────────────────────────────────────────

/** 参与统计的最大周期数。太久远的周期已经不代表当前身体状态。 */
const MAX_SAMPLES = 12
/** 权重衰减因子，w_i = DECAY^i（i=0 为最近一次）。0.85 ≈ 半衰期 4 个周期。 */
const DECAY = 0.85
/** 加权均值与中位数的混合比例。中位数分量负责抗异常值。 */
const MEAN_WEIGHT = 0.6
/** 先验的等效样本量。样本少时预测靠它兜底，样本多了它的影响自然衰减。 */
const PRIOR_STRENGTH = 2
/** 标准差下限，防止「连续 3 个周期都是 28 天」就给出 ±0 天的假精确。 */
const SD_FLOOR = 1.5
/** 默认周期长度先验的标准差。 */
const PRIOR_SD_CYCLE = 4
const PRIOR_SD_PERIOD = 1.5
/** 约 80% 置信区间的 z 值。 */
const Z_80 = 1.2816
/** 无 BBT/LH 数据时的黄体期长度假设。 */
export const DEFAULT_LUTEAL = 14
/** 合理周期长度范围，超出视为异常，排除出统计但仍在日历上显示。 */
const MIN_CYCLE = 15
const MAX_CYCLE = 60
/** 精子存活约 5 天，卵子约 24 小时。 */
const FERTILE_BEFORE = 5
const FERTILE_AFTER = 1
/** 经前不适窗口长度。 */
const PMS_DAYS = 5

// ── 类型 ────────────────────────────────────────────────────────────────

export type Phase =
  | 'menstrual'
  | 'follicular'
  | 'fertile'
  | 'ovulation'
  | 'luteal'
  | 'pms'
  | 'late'
  | 'unknown'

export type Regularity = 'unknown' | 'high' | 'medium' | 'low' | 'irregular'

export interface Cycle {
  /** 周期第一天（CD1）= 首个正式出血日 */
  start: ISODate
  /** 进行中的周期为 null */
  length: number | null
  /** 本次经期持续天数 */
  periodLength: number
  /** 长度超出 15–60 天，不参与统计 */
  anomalous: boolean
  /** 回溯推算的排卵日（仅已结束的周期）；有 LH/BBT 时为实测锚定 */
  ovulation: ISODate | null
  /** 排卵日是实测（LH/BBT）还是按黄体期倒推 */
  ovulationSource: 'lh' | 'bbt' | 'derived' | null
}

export interface Estimate {
  mean: number
  sd: number
  /** 参与统计的有效样本数 */
  n: number
  regularity: Regularity
}

export interface DateRange {
  start: ISODate
  end: ISODate
}

export interface Prediction {
  today: ISODate
  /** 至少记录过一次经期 */
  hasData: boolean
  cycles: Cycle[]
  cycleLength: Estimate
  periodLength: Estimate
  /** 实测得出的黄体期长度；无数据时为 DEFAULT_LUTEAL */
  lutealLength: number
  currentCycleStart: ISODate | null
  /** 今天是周期第几天，从 1 开始 */
  cycleDay: number | null
  /** 预测的下次经期首日与 80% 置信区间 */
  nextPeriod: { expected: ISODate; earliest: ISODate; latest: ISODate } | null
  /** 预测经期的持续区间（用点估计） */
  nextPeriodRange: DateRange | null
  ovulation: { expected: ISODate; earliest: ISODate; latest: ISODate } | null
  /** 核心易孕窗；周期越不规律越宽 */
  fertileWindow: DateRange | null
  phase: Phase
  /** 距离下次经期还有几天；已超期则为负 */
  daysUntilPeriod: number | null
  /** 超期天数，未超期为 null */
  lateBy: number | null
}

export interface PredictInput {
  periods: PeriodLog[]
  today: ISODate
  defaultCycleLen?: number
  defaultPeriodLen?: number
  /** 设置里打开开关后才会参与计算 */
  bbt?: BbtLog[]
  lh?: LhLog[]
}

// ── 基础统计 ────────────────────────────────────────────────────────────

function median(xs: number[]): number {
  if (xs.length === 0) return 0
  const s = [...xs].sort((a, b) => a - b)
  const mid = s.length >> 1
  if (s.length % 2 === 1) return s[mid] as number
  return ((s[mid - 1] as number) + (s[mid] as number)) / 2
}

function classifyRegularity(sd: number, n: number): Regularity {
  if (n === 0) return 'unknown'
  if (sd <= 2) return 'high'
  if (sd <= 4) return 'medium'
  if (sd <= 7) return 'low'
  return 'irregular'
}

/**
 * 加权 + 稳健 + 向先验收缩的估计。
 *
 * values 按时间升序传入（最后一个是最近一次）。
 */
export function estimate(
  values: number[],
  priorMean: number,
  priorSd: number,
): Estimate {
  const recent = values.slice(-MAX_SAMPLES)
  const n = recent.length
  if (n === 0) {
    return { mean: priorMean, sd: priorSd, n: 0, regularity: 'unknown' }
  }

  // 越近的周期权重越高
  const w = recent.map((_, i) => Math.pow(DECAY, n - 1 - i))
  const W = w.reduce((a, b) => a + b, 0)
  const wMean = recent.reduce((acc, v, i) => acc + (w[i] as number) * v, 0) / W

  // 混合中位数：单个离群周期最多只能影响 60% 的分量
  const blended = MEAN_WEIGHT * wMean + (1 - MEAN_WEIGHT) * median(recent)

  // 可靠性权重下的无偏加权方差；n=1 时分母为 0，方差交给先验决定
  const V2 = w.reduce((acc, x) => acc + x * x, 0)
  const denom = W - V2 / W
  const variance =
    denom > 1e-9
      ? recent.reduce(
          (acc, v, i) => acc + (w[i] as number) * (v - wMean) ** 2,
          0,
        ) / denom
      : 0

  // 贝叶斯收缩：样本越多，先验的影响越小
  const mean = (PRIOR_STRENGTH * priorMean + n * blended) / (PRIOR_STRENGTH + n)
  const sd = Math.max(
    SD_FLOOR,
    Math.sqrt((PRIOR_STRENGTH * priorSd ** 2 + n * variance) / (PRIOR_STRENGTH + n)),
  )

  return { mean, sd, n, regularity: classifyRegularity(sd, n) }
}

// ── 从流水提取周期 ──────────────────────────────────────────────────────

/**
 * 周期第一天（CD1）的判定：正式出血日，且前两天都没有正式出血。
 *
 * 「点滴出血」(spot) 不能起始一个周期 —— 经期前一两天的褐色分泌物很常见，
 * 医学上 CD1 指的是真正见红那天。但 spot 会算进经期长度里。
 * 回看两天而不是一天，是为了容忍中间漏记一天不会被误判成两个周期。
 */
export function extractCycles(
  periods: PeriodLog[],
  opts: { bbt?: BbtLog[]; lh?: LhLog[] } = {},
): Cycle[] {
  const bleed = new Set<ISODate>()
  const anyLog = new Set<ISODate>()
  for (const p of periods) {
    anyLog.add(p.date)
    if (p.flow !== 'spot') bleed.add(p.date)
  }

  const candidates = [...bleed]
    .sort((a, b) => toDayNumber(a) - toDayNumber(b))
    .filter((d) => !bleed.has(addDays(d, -1)) && !bleed.has(addDays(d, -2)))

  // 距上一个 CD1 不足 15 天的，几乎一定是记录问题（连续漏记两天，
  // 或把排卵期出血当成了经期），而不是真的又来了一次月经。
  // 直接并入上一个周期，避免生成一堆垃圾周期把「当前周期第几天」算歪。
  const starts: ISODate[] = []
  for (const d of candidates) {
    const prev = starts[starts.length - 1]
    if (prev !== undefined && diffDays(prev, d) < MIN_CYCLE) continue
    starts.push(d)
  }

  const cycles: Cycle[] = []
  for (let i = 0; i < starts.length; i++) {
    const start = starts[i] as ISODate
    const next = starts[i + 1]
    const length = next ? diffDays(start, next) : null
    const anomalous = length !== null && (length < MIN_CYCLE || length > MAX_CYCLE)

    const cap = next ? diffDays(start, next) - 1 : 15
    const periodLength = measurePeriodLength(start, anyLog, Math.min(cap, 15))

    let ovulation: ISODate | null = null
    let ovulationSource: Cycle['ovulationSource'] = null
    if (next) {
      const measured = detectOvulation(start, next, opts)
      if (measured) {
        ovulation = measured.date
        ovulationSource = measured.source
      } else {
        ovulation = addDays(next, -DEFAULT_LUTEAL)
        ovulationSource = 'derived'
      }
    }

    cycles.push({ start, length, periodLength, anomalous, ovulation, ovulationSource })
  }
  return cycles
}

/** 从 CD1 起连续的出血/点滴天数，容忍中间漏记一天。 */
function measurePeriodLength(start: ISODate, anyLog: Set<ISODate>, cap: number): number {
  let len = 1
  let gap = 0
  for (let i = 1; i <= Math.max(cap, 1); i++) {
    const d = addDays(start, i)
    if (anyLog.has(d)) {
      len = i + 1
      gap = 0
    } else if (++gap > 1) {
      break
    }
  }
  return len
}

// ── 实测排卵（BBT / LH）────────────────────────────────────────────────
// 本期没有录入界面，但设置里的开关一打开，这两条路径立刻生效，
// 排卵日就从「按 14 天倒推」升级为实测锚定。

function detectOvulation(
  start: ISODate,
  end: ISODate,
  opts: { bbt?: BbtLog[]; lh?: LhLog[] },
): { date: ISODate; source: 'lh' | 'bbt' } | null {
  const inCycle = <T extends { date: ISODate }>(xs: T[] | undefined) =>
    (xs ?? [])
      .filter((x) => toDayNumber(x.date) >= toDayNumber(start) && toDayNumber(x.date) < toDayNumber(end))
      .sort((a, b) => toDayNumber(a.date) - toDayNumber(b.date))

  // LH 强阳后 24–36 小时排卵，取次日
  const peaks = inCycle(opts.lh).filter((x) => x.result === 'peak')
  const lastPeak = peaks[peaks.length - 1]
  if (lastPeak) return { date: addDays(lastPeak.date, 1), source: 'lh' }

  // BBT「三高一低」：连续 3 天高于前 6 天的最高值，排卵日 = 升温前一天
  const temps = inCycle(opts.bbt)
  for (let i = 6; i + 2 < temps.length; i++) {
    const window = temps.slice(i - 6, i)
    const coverline = Math.max(...window.map((t) => t.tempC))
    const a = temps[i]
    const b = temps[i + 1]
    const c = temps[i + 2]
    if (!a || !b || !c) continue
    if (a.tempC > coverline && b.tempC > coverline && c.tempC >= coverline + 0.2) {
      return { date: addDays(a.date, -1), source: 'bbt' }
    }
  }
  return null
}

/** 从有实测排卵日的历史周期里估计真实黄体期长度。 */
function estimateLuteal(cycles: Cycle[]): number {
  const samples: number[] = []
  for (let i = 0; i < cycles.length; i++) {
    const c = cycles[i] as Cycle
    const next = cycles[i + 1]
    if (!next || !c.ovulation || c.ovulationSource === 'derived') continue
    const len = diffDays(c.ovulation, next.start)
    if (len >= 8 && len <= 18) samples.push(len)
  }
  if (samples.length < 2) return DEFAULT_LUTEAL
  return Math.round(median(samples))
}

// ── 主预测 ──────────────────────────────────────────────────────────────

export function predict(input: PredictInput): Prediction {
  const { periods, today } = input
  const priorCycle = input.defaultCycleLen ?? 28
  const priorPeriod = input.defaultPeriodLen ?? 5

  const cycles = extractCycles(periods, { bbt: input.bbt, lh: input.lh })

  const empty: Prediction = {
    today,
    hasData: false,
    cycles,
    cycleLength: { mean: priorCycle, sd: PRIOR_SD_CYCLE, n: 0, regularity: 'unknown' },
    periodLength: { mean: priorPeriod, sd: PRIOR_SD_PERIOD, n: 0, regularity: 'unknown' },
    lutealLength: DEFAULT_LUTEAL,
    currentCycleStart: null,
    cycleDay: null,
    nextPeriod: null,
    nextPeriodRange: null,
    ovulation: null,
    fertileWindow: null,
    phase: 'unknown',
    daysUntilPeriod: null,
    lateBy: null,
  }
  if (cycles.length === 0) return empty

  // 当前周期 = 最后一个开始于今天或之前的周期
  const startedCycles = cycles.filter((c) => toDayNumber(c.start) <= toDayNumber(today))
  const current = startedCycles[startedCycles.length - 1] ?? (cycles[0] as Cycle)
  const currentCycleStart = current.start

  const lengths = cycles
    .filter((c): c is Cycle & { length: number } => c.length !== null && !c.anomalous)
    .map((c) => c.length)
  const cycleLength = estimate(lengths, priorCycle, PRIOR_SD_CYCLE)
  const periodLength = estimate(
    cycles.map((c) => c.periodLength),
    priorPeriod,
    PRIOR_SD_PERIOD,
  )
  const lutealLength = estimateLuteal(cycles)

  // 下次经期：从当前周期第一天推 L̂ 天，区间是 ±1.28σ（约 80%）
  const half = Z_80 * cycleLength.sd
  const expected = addDays(currentCycleStart, Math.round(cycleLength.mean))
  const earliest = addDays(currentCycleStart, Math.round(cycleLength.mean - half))
  const latest = addDays(currentCycleStart, Math.round(cycleLength.mean + half))
  const nextPeriod = { expected, earliest, latest }
  const nextPeriodRange: DateRange = {
    start: expected,
    end: addDays(expected, Math.max(0, Math.round(periodLength.mean) - 1)),
  }

  // 排卵日从预测经期倒推 —— 黄体期比卵泡期稳定得多，倒推比正推准
  const ovExpected = addDays(expected, -lutealLength)
  const ovulation = {
    expected: ovExpected,
    earliest: addDays(earliest, -lutealLength),
    latest: addDays(latest, -lutealLength),
  }

  // 易孕窗以点估计为核心，再按周期波动适度放宽（最多各加 3 天）
  const slack = Math.min(3, Math.max(0, Math.round((cycleLength.sd - 2) / 2)))
  const fertileWindow: DateRange = {
    start: addDays(ovExpected, -(FERTILE_BEFORE + slack)),
    end: addDays(ovExpected, FERTILE_AFTER + slack),
  }

  const bleedingToday = periods.some((p) => p.date === today && p.flow !== 'spot')
  const daysUntilPeriod = diffDays(today, expected)
  const lateBy = daysUntilPeriod < 0 ? -daysUntilPeriod : null

  const phase = resolvePhase({
    today,
    bleedingToday,
    expected,
    ovExpected,
    fertileWindow,
    daysUntilPeriod,
  })

  return {
    today,
    hasData: true,
    cycles,
    cycleLength,
    periodLength,
    lutealLength,
    currentCycleStart,
    cycleDay: diffDays(currentCycleStart, today) + 1,
    nextPeriod,
    nextPeriodRange,
    ovulation,
    fertileWindow,
    phase,
    daysUntilPeriod,
    lateBy,
  }
}

function resolvePhase(a: {
  today: ISODate
  bleedingToday: boolean
  expected: ISODate
  ovExpected: ISODate
  fertileWindow: DateRange
  daysUntilPeriod: number
}): Phase {
  if (a.bleedingToday) return 'menstrual'
  if (a.daysUntilPeriod < 0) return 'late'
  if (a.today === a.ovExpected) return 'ovulation'
  if (isBetween(a.today, a.fertileWindow.start, a.fertileWindow.end)) return 'fertile'
  if (a.daysUntilPeriod <= PMS_DAYS) return 'pms'
  if (toDayNumber(a.today) < toDayNumber(a.ovExpected)) return 'follicular'
  return 'luteal'
}

// ── 日历标注 ────────────────────────────────────────────────────────────

export interface DayInfo {
  date: ISODate
  /** 实际记录的流量 */
  flow: PeriodLog['flow'] | null
  /** 预测中的经期日（尚未发生） */
  predictedPeriod: boolean
  /** 易孕窗内（历史周期为回溯推算） */
  fertile: boolean
  /** 排卵日 */
  ovulation: boolean
  /** 该日属于哪个周期的第几天 */
  cycleDay: number | null
  isToday: boolean
  isFuture: boolean
}

/**
 * 生成 [from, to] 每一天的标注，日历直接渲染。
 *
 * 过去的日子用真实记录 + 回溯推算的排卵日；未来的日子用预测，
 * 并按平均周期长度往后投影多个周期，这样往后翻月份也有内容。
 */
export function buildDayMap(
  prediction: Prediction,
  periods: PeriodLog[],
  from: ISODate,
  to: ISODate,
): Map<ISODate, DayInfo> {
  const flowByDate = new Map<ISODate, PeriodLog['flow']>()
  for (const p of periods) flowByDate.set(p.date, p.flow)

  const predictedPeriodDays = new Set<ISODate>()
  const fertileDays = new Set<ISODate>()
  const ovulationDays = new Set<ISODate>()

  // 历史周期：回溯标注排卵日与易孕窗
  for (const c of prediction.cycles) {
    if (!c.ovulation) continue
    ovulationDays.add(c.ovulation)
    for (let d = -FERTILE_BEFORE; d <= FERTILE_AFTER; d++) {
      fertileDays.add(addDays(c.ovulation, d))
    }
  }

  // 未来周期：从预测的下次经期起，按平均长度往后投影 4 个周期
  if (prediction.nextPeriod && prediction.hasData) {
    const step = Math.round(prediction.cycleLength.mean)
    const periodLen = Math.max(1, Math.round(prediction.periodLength.mean))
    const slack = Math.min(
      3,
      Math.max(0, Math.round((prediction.cycleLength.sd - 2) / 2)),
    )
    for (let k = 0; k < 4; k++) {
      const start = addDays(prediction.nextPeriod.expected, k * step)
      if (toDayNumber(start) > toDayNumber(to) + step) break
      for (let i = 0; i < periodLen; i++) predictedPeriodDays.add(addDays(start, i))
      const ov = addDays(start, -prediction.lutealLength)
      ovulationDays.add(ov)
      for (let d = -(FERTILE_BEFORE + slack); d <= FERTILE_AFTER + slack; d++) {
        fertileDays.add(addDays(ov, d))
      }
    }
  }

  // 每天归属哪个周期：取最后一个开始于该日或之前的周期
  const starts = prediction.cycles.map((c) => c.start)

  const out = new Map<ISODate, DayInfo>()
  for (let n = toDayNumber(from); n <= toDayNumber(to); n++) {
    const date = addDays(from, n - toDayNumber(from))
    const flow = flowByDate.get(date) ?? null

    let cycleDay: number | null = null
    for (let i = starts.length - 1; i >= 0; i--) {
      const s = starts[i] as ISODate
      if (toDayNumber(s) <= n) {
        cycleDay = n - toDayNumber(s) + 1
        break
      }
    }

    out.set(date, {
      date,
      flow,
      // 实际记录优先：已经记了流量就不再显示「预测经期」
      predictedPeriod: flow === null && predictedPeriodDays.has(date),
      fertile: fertileDays.has(date),
      ovulation: ovulationDays.has(date),
      cycleDay,
      isToday: date === prediction.today,
      isFuture: toDayNumber(date) > toDayNumber(prediction.today),
    })
  }
  return out
}

// ── 症状洞察 ────────────────────────────────────────────────────────────

export interface SymptomInsight {
  symptom: string
  /** 通常出现在周期第几天 */
  typicalCycleDay: number
  /** 通常在经期前几天出现（负数表示经期开始后） */
  daysBeforePeriod: number
  occurrences: number
  cyclesSeen: number
}

/**
 * 统计每个症状相对周期日的分布，得出「你通常在第 24 天前后开始腰酸」这类结论。
 * 样本不足（少于 3 次、少于 2 个周期）就不出结论 —— 宁可不说，也不说错。
 */
export function symptomInsights(
  cycles: Cycle[],
  symptoms: SymptomLog[],
): SymptomInsight[] {
  if (cycles.length === 0) return []

  const byDay = new Map<string, { days: number[]; toPeriod: number[]; cycles: Set<string> }>()

  for (const log of symptoms) {
    if (log.symptoms.length === 0) continue
    // 找到该记录属于哪个周期
    let owner: Cycle | null = null
    for (let i = cycles.length - 1; i >= 0; i--) {
      const c = cycles[i] as Cycle
      if (toDayNumber(c.start) <= toDayNumber(log.date)) {
        owner = c
        break
      }
    }
    if (!owner) continue
    const cd = diffDays(owner.start, log.date) + 1
    // 超出合理范围的（多半是漏记造成的超长周期）不参与统计
    if (cd < 1 || cd > MAX_CYCLE) continue
    const toPeriod = owner.length !== null ? owner.length - cd + 1 : Number.NaN

    for (const s of log.symptoms) {
      let e = byDay.get(s)
      if (!e) {
        e = { days: [], toPeriod: [], cycles: new Set() }
        byDay.set(s, e)
      }
      e.days.push(cd)
      if (!Number.isNaN(toPeriod)) e.toPeriod.push(toPeriod)
      e.cycles.add(owner.start)
    }
  }

  const out: SymptomInsight[] = []
  for (const [symptom, e] of byDay) {
    if (e.days.length < 3 || e.cycles.size < 2) continue
    out.push({
      symptom,
      typicalCycleDay: Math.round(median(e.days)),
      daysBeforePeriod: e.toPeriod.length > 0 ? Math.round(median(e.toPeriod)) : 0,
      occurrences: e.days.length,
      cyclesSeen: e.cycles.size,
    })
  }
  return out.sort((a, b) => b.occurrences - a.occurrences)
}
